// ProfileGateway: simplified to basic role + PII form only (archetype/adhikara/acharya/raja/overview/proposal steps removed from creation flow per request).
// Legacy roleGateway fields preserved in data + save for compatibility with other modules (RajaDashboard, gatewayPolicy, etc.).
// Seva provider config and advanced details are additional/on-need (not part of the basic required creation UI).

import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import {
  uidToHue,
  useAuth,
  type UserProfile,
} from '../contexts/AuthContext';
import { useGlossary } from '../contexts/GlossaryContext';
import { calculateGatewayProgress } from '../lib/gatewayPolicy';
import { getProfileView } from '../lib/api';
import type { FilteredProfile } from '../lib/api';
import '../styles/UserProfile.css';

// ── My View: read-only section component ─────────────────────────────────
interface MvField { label: string; value: string; private?: boolean }
function MyViewSection({ title, fields }: { title: string; fields: MvField[] }) {
  return (
    <div className="up-my-view__section">
      <div className="up-my-view__section-title">{title}</div>
      <dl className="up-my-view__dl">
        {fields.map(f => (
          <div key={f.label} className={`up-my-view__row ${!f.value ? 'up-my-view__row--empty' : ''}`}>
            <dt>{f.label}{f.private && <span className="up-my-view__vis"> 🔒</span>}</dt>
            <dd>{f.value || <em className="up-empty-val">—</em>}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// ── Public View component ─────────────────────────────────────────────────
function PublicViewPanel({ uid, locale }: { uid: string; locale: string }) {
  const [data, setData] = useState<FilteredProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    getProfileView(uid)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError(locale === 'en' ? 'Profile not publicly visible.' : 'प्रोफ़ाइल सार्वजनिक नहीं है।'); setLoading(false); });
  }, [uid, locale]);

  if (loading) return <div className="up-my-view" style={{ padding: '24px', color: '#64748b' }}>⏳ {locale === 'en' ? 'Loading public view…' : 'सार्वजनिक दृष्टि लोड हो रही है…'}</div>;
  if (error || !data) return <div className="up-my-view" style={{ padding: '24px', color: '#94a3b8' }}>{error}</div>;

  const vis = (v: string | undefined) => v ? `✅ ${v}` : '';
  return (
    <div className="up-my-view">
      <div className="up-my-view__toolbar">
        <span className="up-my-view__note">
          🌐 {locale === 'en' ? 'This is what anonymous visitors see.' : 'अनजान आगंतुकों को यही दिखता है।'}
        </span>
      </div>
      <MyViewSection title={locale === 'en' ? 'Public info' : 'सार्वजनिक जानकारी'} fields={[
        { label: 'Alias',           value: data.alias ?? '' },
        { label: 'Role',            value: data.profileKind ?? '' },
        { label: 'Level',           value: data.learningLevel ?? '' },
        { label: 'Script',          value: data.preferredScript ?? '' },
        { label: 'Collaboration',   value: data.collaborationMode ?? '' },
        { label: 'Study areas',     value: vis(Array.isArray(data.studyAreas) ? data.studyAreas.join(', ') : (data.studyAreas ?? '')) },
        { label: 'Qualification',   value: vis(data.qualification) },
        { label: 'Session modes',   value: (data.sessionModes ?? []).join(', ') },
        { label: 'Availability',    value: (data.availability ?? []).join(', ') },
        { label: 'Looking for',     value: data.lookingFor ?? '' },
        { label: 'Time zone',       value: data.timeZone ?? '' },
      ]} />
    </div>
  );
}

interface ProfileGatewayProps {
  onNavigate: (path: string) => void;
  onNavigateHome: () => void;
}

export default function ProfileGateway({ onNavigate, onNavigateHome }: ProfileGatewayProps) {
  const { t, locale } = useGlossary();
  const {
    user,
    profile,
    policy,
    signOut,
    updateProfile,
    exportProfile,
    importProfile,
    syncDiscovery,
    removeFromDiscovery,
    generateInvite,
    deleteAccount,
  } = useAuth();

  const [saveMsg, setSaveMsg] = useState('');
  const [viewTab, setViewTab] = useState<'fill' | 'self' | 'public'>('fill');
  const [importError, setImportError] = useState('');
  const [importKey, setImportKey] = useState(0);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [discoverySyncing, setDiscoverySyncing] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const returnTo = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('return_to') ?? '';
  }, []);

  useEffect(() => {
    if (!profile) return;
    const gate = calculateGatewayProgress(profile, policy);
    if (gate.ready && !profile.roleGateway.completedAt) {
      updateProfile({
        roleGateway: {
          ...profile.roleGateway,
          completedAt: new Date().toISOString(),
        },
      });
    }
  }, [policy, profile, updateProfile]);

  if (!user || !profile) {
    return (
      <div className="up-container">
        <p className="up-empty">{t('profile.empty.signIn')}</p>
        <button className="up-btn-secondary" onClick={onNavigateHome}>{t('profile.button.back')}</button>
      </div>
    );
  }

  const gate = calculateGatewayProgress(profile, policy);
  const completion = gate.percent;

  const hue = uidToHue(profile.uid);
  const avatarLabel = profile.alias ? profile.alias.slice(0, 2).toUpperCase() : '👤';
  const stageLabel = gate.stageKey ? t(`profileGateway.stage.${gate.stageKey}`) : t('profileGateway.stage.awaiting');
  const roleDescription = (role: any) => t(`profileGateway.role.${role || 'select'}.description`);
  const currentRoleDescription = roleDescription(profile.roleGateway.selectedRole);

  const setProfileField = (field: keyof UserProfile, value: string | boolean | string[]) => {
    updateProfile({ [field]: value } as Partial<UserProfile>);
  };

  const handleDiscoverabilityToggle = async (enabled: boolean) => {
    setDiscoverySyncing(true);
    try {
      const nextProfile = { ...profile, isDiscoverable: enabled };
      updateProfile(nextProfile);
      if (enabled) await syncDiscovery(nextProfile);
      else await removeFromDiscovery(nextProfile);
    } finally {
      setDiscoverySyncing(false);
    }
  };

  const handleGenerateInvite = async () => {
    setInviteLoading(true);
    try {
      const code = await generateInvite();
      setInviteCode(code);
    } catch {
      setSaveMsg(t('profile.error.invite'));
      setTimeout(() => setSaveMsg(''), 4000);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleImport = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await importProfile(file);
      setImportError('');
      setSaveMsg(t('profile.restore.success'));
      setTimeout(() => setSaveMsg(''), 3000);
    } catch {
      setImportError(t('profile.error.import'));
    }
    setImportKey((value) => value + 1);
  };

  const copyProfileCode = () => {
    navigator.clipboard.writeText(profile.uid).then(() => {
      setSaveMsg(t('profile.copy.success'));
      setTimeout(() => setSaveMsg(''), 2000);
    });
  };



  // Basic profile save (archetype, adhikara, acharya, raja, overview, proposal steps removed from UI flow).
  // Mirrors minimal data into legacy roleGateway for compat with policy/governance modules.
  const saveBasicProfile = () => {
    // Map simple role to legacy gateway selectedRole for compatibility
    let selected: any = 'shiksharthi';
    if (profile.role === 'Educator') selected = 'acharya';
    else if (profile.role === 'Service Provider') selected = 'nirmata';
    else if (profile.role === 'Service Consumer') selected = 'shiksharthi';  // consumers primarily as learners/seekers

    updateProfile({
      role: profile.role || 'Shiksharthi / Learner',
      alias: profile.alias || '',
      dob: profile.dob || '',
      pincode: profile.pincode || '',
      cell: profile.cell || '',
      emailContact: profile.emailContact || '',
      roleGateway: {
        ...(profile.roleGateway || {}),
        selectedRole: selected,
        primaryArchetype: profile.roleGateway?.primaryArchetype || 'anveshak',
        secondaryArchetype: profile.roleGateway?.secondaryArchetype || '',
        city: profile.roleGateway?.city || '',
        adhikara: profile.roleGateway?.adhikara || { hridaya: null, vak: null, jnana: null, karma: null, phala: null },
        acharya: profile.roleGateway?.acharya || { shravana: false, smarana: false, samvada: false, vinaya: false, seva: false, niyama: false, dambhahin: false },
        raja: profile.roleGateway?.raja || {},
        domainPledges: profile.roleGateway?.domainPledges || { learning: false, service: false, finance: false, family: false, community: false },
        proposal: profile.roleGateway?.proposal || { primaryCategory: '', secondaryCategory: '', rolesNeeded: '', studiedProjects: [] },
      },
    } as Partial<UserProfile>);

    setViewTab('self');
  };

  return (
    <div className="up-container gateway-container">
      {returnTo && (
        <div className="gateway-return-banner">
          <span>{t('profileGateway.returnBanner.message')}</span>
          <button type="button" className="gateway-return-skip" onClick={() => onNavigate(returnTo)}>
            {t('profileGateway.returnBanner.skip')} →
          </button>
        </div>
      )}
      <div className="up-header">
        <div className="up-header-left">
          <button className="up-back-btn" onClick={onNavigateHome}>{t('profile.button.back')}</button>
          <h1 className="up-heading">{t('profile.title')}</h1>
        </div>
        <div className="up-header-actions">
          <button className="up-copy-btn" onClick={copyProfileCode} title={t('profile.copy')}>
            📋 <span>{t('profile.copy')}</span>
          </button>
        </div>
      </div>

      <div className="up-privacy-badge">{t('profile.privacy.badge')}</div>

      <div className="up-hero">
        <div className="up-avatar" style={{ background: `hsl(${hue},55%,45%)` }} aria-hidden="true">
          <span className="up-avatar-initials">{avatarLabel}</span>
        </div>
        <div className="up-hero-info">
          <div className="up-name">{profile.alias || <em style={{ color: '#bbb' }}>{t('profile.empty.alias')}</em>}</div>
          <div className="up-uid-row">
            <div className="up-uid">ID: {profile.uid.slice(0, 10)}…</div>
          </div>
          <div className="up-completion-label">
            {t('profile.completion.label')}: <strong>{completion}%</strong>
          </div>
          <div className="up-meter-bg">
            <div className="up-meter-fill" style={{ width: `${completion}%` }} />
          </div>
          <p className="up-completion-hint">{currentRoleDescription}</p>
        </div>
      </div>

      {saveMsg && <div className="up-save-msg">{saveMsg}</div>}

      {/* ── Three-tab navigation ──────────────────────────────────────────── */}
      <div className="up-view-tabs" role="tablist" aria-label={locale === 'en' ? 'Profile views' : 'प्रोफ़ाइल दृष्टि'}>
        <button
          className={`up-view-tab ${viewTab === 'fill' ? 'up-view-tab--active' : ''}`}
          role="tab" aria-selected={viewTab === 'fill'}
          onClick={() => setViewTab('fill')}
        >
          ✏️ {locale === 'en' ? 'Edit Basic Profile' : 'बेसिक प्रोफ़ाइल संपादित करें'}
        </button>
        <button
          className={`up-view-tab ${viewTab === 'self' ? 'up-view-tab--active' : ''}`}
          role="tab" aria-selected={viewTab === 'self'}
          onClick={() => setViewTab('self')}
        >
          👤 {t('profile.view.self')}
        </button>
        <button
          className={`up-view-tab ${viewTab === 'public' ? 'up-view-tab--active' : ''}`}
          role="tab" aria-selected={viewTab === 'public'}
          onClick={() => setViewTab('public')}
        >
          🌐 {t('profile.view.others')}
        </button>
      </div>

      {/* ── TAB: Simple Basic Profile Form (elaborate steps removed per request) ─ */}
      {viewTab === 'fill' && (
        <div className="up-my-view">
          <div className="up-my-view__toolbar">
            <span className="up-my-view__note">
              {locale === 'en' ? 'Basic required: Role + DoB, Pincode, Cell, e-Mail. (The steps archetype/adhikara/acharya/raja/overview/proposal have been removed from creation.)' : 'आवश्यक: भूमिका + जन्मतिथि, पिनकोड, सेल, ई-मेल। (archetype आदि विस्तृत चरण हटाए गए।)'}
            </span>
          </div>

          <div className="gateway-form-grid">
            <label>
              Role
              <select value={profile.role || 'Shiksharthi / Learner'} onChange={(e) => setProfileField('role', e.target.value as any)}>
                <option value="Shiksharthi / Learner">Shiksharthi / Learner</option>
                <option value="Educator">Educator</option>
                <option value="Service Provider">Service Provider</option>
                <option value="Service Consumer">Service Consumer</option>
              </select>
            </label>

            <label>
              Alias (display name)
              <input value={profile.alias || ''} onChange={(e) => setProfileField('alias', e.target.value)} placeholder="your-alias" />
            </label>

            <label>
              Date of Birth (DoB)
              <input type="date" value={profile.dob || ''} onChange={(e) => setProfileField('dob', e.target.value)} />
            </label>

            <label>
              Pincode
              <input value={profile.pincode || ''} onChange={(e) => setProfileField('pincode', e.target.value.replace(/[^0-9]/g, '').slice(0, 6))} placeholder="e.g. 176057" />
            </label>

            <label>
              Cell / Phone
              <input value={profile.cell || ''} onChange={(e) => setProfileField('cell', e.target.value.replace(/[^0-9+\- ]/g, '').slice(0, 15))} placeholder="+91 98765 43210" />
            </label>

            <label>
              Contact e-Mail
              <input type="email" value={profile.emailContact || ''} onChange={(e) => setProfileField('emailContact', e.target.value)} placeholder="you@example.com" />
            </label>
          </div>

          <div className="gateway-action-row" style={{ marginTop: '1rem' }}>
            <button className="up-btn-primary" onClick={saveBasicProfile}>
              {locale === 'en' ? 'Save Basic Profile' : 'बेसिक प्रोफ़ाइल सेव करें'}
            </button>
            <button className="up-btn-secondary" onClick={() => setViewTab('self')}>
              {locale === 'en' ? 'Cancel / View' : 'रद्द / देखें'}
            </button>
          </div>

          <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '1rem' }}>
            {locale === 'en' ? 'The elaborate steps (archetype, adhikara, acharya, raja, overview, proposal) have been removed from the profile creation flow. Role + basic PII (DoB, pincode, cell, email) are the required fields. Additional details remain in stored data only for compatibility where previously entered.' : 'विस्तृत चरण (archetype, adhikara आदि) प्रोफ़ाइल निर्माण फ्लो से हटा दिए गए हैं। केवल भूमिका + बेसिक व्यक्तिगत जानकारी आवश्यक है।'}
          </p>
        </div>
      )}

      {/* ── TAB: My View — full read-only card ───────────────────────────── */}
      {viewTab === 'self' && (
        <div className="up-my-view">
          <div className="up-my-view__toolbar">
            <span className="up-my-view__note">
              🔐 {locale === 'en' ? 'Private fields (🔒) are only visible to you.' : 'निजी फ़ील्ड (🔒) केवल आपको दिखती हैं।'}
            </span>
            <button className="up-btn-primary up-btn-primary--compact" onClick={() => setViewTab('fill')}>
              ✏️ {locale === 'en' ? 'Fill / Edit' : 'भरें / संपादित करें'}
            </button>
          </div>
          <MyViewSection title={locale === 'en' ? 'Identity' : 'पहचान'} fields={[
            { label: t('profile.field.alias'),          value: profile.alias,              private: false },
            { label: t('profile.field.nativeLanguage'), value: profile.nativeLanguage,     private: true },
            { label: t('profile.field.age'),            value: profile.age,                private: true },
          ]} />
          <MyViewSection title={locale === 'en' ? 'Role & Stage' : 'भूमिका और स्तर'} fields={[
            { label: t('profileGateway.step.role'),      value: t(`profileGateway.role.${profile.roleGateway.selectedRole || 'select'}.label`) },
            { label: locale === 'en' ? 'Stage' : 'अधिकार-स्तर', value: stageLabel },
            { label: locale === 'en' ? 'Completion' : 'पूर्णता', value: `${completion}%` },
          ]} />
          <MyViewSection title={locale === 'en' ? 'Study preferences' : 'अध्ययन वरीयताएं'} fields={[
            { label: t('profile.field.level'),         value: profile.learningLevel,       private: false },
            { label: t('profile.field.script'),        value: profile.preferredScript,     private: false },
            { label: t('profile.field.studyAreas'),    value: profile.studyAreas,          private: true },
            { label: t('profile.field.learningGoals'), value: profile.learningGoals,       private: true },
            { label: t('profile.field.qualification'), value: profile.qualification,       private: true },
          ]} />
          <MyViewSection title={locale === 'en' ? 'Collaboration' : 'सहयोग'} fields={[
            { label: t('profile.field.timeZone'),      value: profile.timeZone,            private: true },
            { label: t('profile.field.sessionModes'),  value: (profile.sessionModes ?? []).join(', '), private: true },
            { label: t('profile.field.availability'),  value: (profile.availability ?? []).join(', '), private: true },
            { label: t('profile.field.lookingFor'),    value: profile.lookingFor ?? '',    private: true },
            { label: t('profile.field.collaborationMode'), value: profile.collaborationMode ?? '', private: false },
          ]} />
          <MyViewSection title={locale === 'en' ? 'Details' : 'विवरण'} fields={[
            { label: t('profile.field.pincode'), value: profile.pincode ?? '', private: true },
          ]} />
        </div>
      )}

      {/* ── TAB: Public View ─────────────────────────────────────────────── */}
      {viewTab === 'public' && <PublicViewPanel uid={profile.uid} locale={locale} />}

      {/* ── Sections always shown (portability, discovery, invite, danger) ─ */}
      <div className="up-portability">
        <div className="up-section-title">{t('profile.section.portability')}</div>
        <p className="up-portability-note">{t('profile.portability.note')}</p>
        <div className="up-portability-btns">
          <button className="up-btn-export" onClick={exportProfile}>{t('profile.button.export')}</button>
          <label className="up-btn-import" style={{ cursor: 'pointer' }}>
            {t('profile.button.import')}
            <input
              key={importKey}
              type="file"
              accept=".json"
              style={{ display: 'none' }}
              onChange={handleImport}
            />
          </label>
        </div>
        {importError && <p className="up-import-error">{importError}</p>}
      </div>

      <div className="up-discovery-panel">
        <div className="up-section-title">{t('profile.section.peerDiscovery')}</div>
        <p className="up-discovery-note">{t('profile.discovery.note')}</p>
        <div className="up-discovery-toggle-row">
          <span className="up-discovery-status">
            {profile.isDiscoverable ? t('profile.discovery.visible') : t('profile.discovery.hidden')}
          </span>
          <button
            className={`up-toggle-btn ${profile.isDiscoverable ? 'up-toggle-btn--on' : ''}`}
            disabled={discoverySyncing}
            onClick={() => handleDiscoverabilityToggle(!profile.isDiscoverable)}
          >
            {discoverySyncing ? '⏳' : profile.isDiscoverable ? t('profile.toggle.off') : t('profile.toggle.on')}
          </button>
        </div>
      </div>

      <div className="up-invite-panel">
        <div className="up-section-title">{t('profile.section.invite')}</div>
        <p className="up-invite-note">{t('profile.invite.note')}</p>
        <div className="up-invite-row">
          <button className="up-btn-primary" onClick={handleGenerateInvite} disabled={inviteLoading}>
            {inviteLoading ? '⏳' : t('profile.button.newInvite')}
          </button>
          {inviteCode && (
            <div className="up-invite-code-box">
              <span className="up-invite-code">{inviteCode}</span>
            </div>
          )}
        </div>
      </div>

      <div className="up-danger-zone">
        <div className="up-section-title up-danger-title">{t('profile.section.delete')}</div>
        <p className="up-danger-note">{t('profile.delete.note')}</p>
        {!deleteConfirm ? (
          <button className="up-btn-danger" onClick={() => setDeleteConfirm(true)}>
            {t('profile.button.delete')}
          </button>
        ) : (
          <div className="up-delete-confirm">
            <p className="up-delete-confirm-text">{t('profile.delete.confirm')}</p>
            <div className="up-delete-confirm-btns">
              <button
                className="up-btn-danger"
                disabled={deleteLoading}
                onClick={async () => {
                  setDeleteLoading(true);
                  try {
                    await deleteAccount();
                    onNavigateHome();
                  } catch {
                    setSaveMsg(t('profile.error.delete'));
                  } finally {
                    setDeleteLoading(false);
                  }
                }}
              >
                {deleteLoading ? '⏳' : t('profile.button.confirmDelete')}
              </button>
              <button className="up-btn-secondary" onClick={() => setDeleteConfirm(false)} disabled={deleteLoading}>
                {t('profile.button.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="up-signout">
        <button className="up-btn-signout" onClick={async () => { await signOut(); onNavigateHome(); }}>
          {t('profile.button.signOut')}
        </button>
        <p className="up-signout-note">{t('profileGateway.signout.note')}</p>
      </div>
    </div>
  );
}
